import { useEffect, useId, useState } from 'react';
import {
  defaultReviewAgainOn,
  facets,
  fieldValue,
  primaryPhoto,
  sessionQueue,
  tallyIntentions,
  today,
  type AnyEvent,
  type Intention,
  type Manifest,
  type ReviewScope,
  type SearchIndex,
} from '@intentionalist/shared';
import { InvestPrompt } from './InvestPrompt.js';
import { setIntentionWith } from './mutations.js';
import { usePhotoSources } from './usePhotoSources.js';
import type { Replica } from './replica.js';

/**
 * The guided review session (FR-5.6 to FR-5.10).
 *
 * One item at a time, photo first, with the figures that actually change a mind
 * -- what it cost per use, how long since it was last used -- rather than the
 * whole record. The product's argument is that deciding is easier when the
 * question is narrow, so the screen asks exactly one.
 *
 * Nothing here recommends a verdict. `NFR-SAFE-2` is explicit that the product
 * asks for a decision without suggesting one, which is why the four buttons are
 * presented identically and nothing is highlighted as the obvious answer.
 */
interface StoredSession {
  readonly scope: ReviewScope;
  readonly handled: string[];
  readonly decided: number;
  readonly startedAt: number;
}

export function ReviewSession({
  replica,
  manifest,
  version,
  index,
  onWrite,
  onLeave,
}: {
  replica: Replica;
  manifest: Manifest;
  version: number;
  index: SearchIndex;
  onWrite: (events: AnyEvent[]) => void;
  /** Leaves the session for the catalog. The session itself survives (FR-5.10). */
  onLeave: () => void;
}) {
  const [session, setSession] = useState<StoredSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<ReviewScope>({});
  const [uncertainFor, setUncertainFor] = useState<string | null>(null);
  const [reviewAgainOn, setReviewAgainOn] = useState('');
  const [reason, setReason] = useState('');
  const ids = { verdict: useId() };
  /** FR-8.3: the item just marked Invest, awaiting an answer about an entry. */
  const [investFor, setInvestFor] = useState<{ itemId: string; name: string } | null>(null);

  useEffect(() => {
    void replica.reviewSession<StoredSession>().then((stored) => {
      setSession(stored);
      setLoading(false);
    });
  }, [replica]);

  const persist = (next: StoredSession | null) => {
    setSession(next);
    void (next === null ? replica.clearReviewSession() : replica.saveReviewSession(next));
  };

  if (loading) {
    return (
      <p className="muted" role="status">
        looking for a session in progress…
      </p>
    );
  }

  if (session === null) {
    return (
      <ScopeForm
        manifest={manifest}
        version={version}
        scope={scope}
        onChange={setScope}
        onStart={() => persist({ scope, handled: [], decided: 0, startedAt: Date.now() })}
        index={index}
      />
    );
  }

  const handled = new Set(session.handled);
  const remaining = sessionQueue(manifest, session.scope, handled, index);
  const item = remaining[0];

  const advance = (itemId: string, decided: boolean) => {
    persist({
      ...session,
      handled: [...session.handled, itemId],
      decided: session.decided + (decided ? 1 : 0),
    });
    setUncertainFor(null);
    setReason('');
    setReviewAgainOn('');
  };

  if (item === undefined) {
    return (
      <div className="review">
        {investFor !== null && (
          <InvestPrompt subject={investFor} onWrite={onWrite} onDone={() => setInvestFor(null)} />
        )}

        <h2>Review complete</h2>
        <p>
          {session.decided} decision{session.decided === 1 ? '' : 's'} recorded.
        </p>
        <div className="actions">
          <button type="button" onClick={() => persist(null)}>
            Start another
          </button>
          <button
            type="button"
            onClick={() => {
              persist(null);
              onLeave();
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const record = (intention: Intention) => {
    if (intention === 'uncertain' && uncertainFor !== item.itemId) {
      // FR-5.5: prompted by default when choosing Uncertain. The other three
      // verdicts stay one tap, because only this one says "ask me again".
      setUncertainFor(item.itemId);
      setReviewAgainOn(defaultReviewAgainOn(today()));
      return;
    }
    onWrite(setIntentionWith(item.itemId, intention, { reason, reviewAgainOn }));
    // FR-8.3: an Invest verdict is a statement that the need is real, which is
    // exactly what an investment entry records -- so it is offered here rather
    // than left to be remembered later. Offered, not created: the verdict stands
    // on its own and this is a second decision (NFR-SAFE-2).
    if (intention === 'invest') {
      setInvestFor({
        itemId: item.itemId,
        name: String(fieldValue(item, 'name') ?? ''),
      });
    }
    advance(item.itemId, true);
  };

  return (
    <div className="review">
      {investFor !== null && (
        <InvestPrompt subject={investFor} onWrite={onWrite} onDone={() => setInvestFor(null)} />
      )}

      <ReviewCard
        replica={replica}
        item={item}
        remaining={remaining.length}
        decided={session.decided}
      />

      <div className="field">
        <label htmlFor="review-reason">Reason (optional)</label>
        <input
          id="review-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoComplete="off"
        />
      </div>

      {uncertainFor === item.itemId && (
        <div className="field">
          <label htmlFor="review-again">Reconsider on</label>
          <input
            id="review-again"
            type="date"
            value={reviewAgainOn}
            onChange={(e) => setReviewAgainOn(e.target.value)}
          />
          <p className="muted hint">It will come back to the top of a session on this date.</p>
        </div>
      )}

      {/*
        One verdict, four answers -- said in words as well as in layout.
        The four were a row of buttons above another row of buttons, which left
        the reader to infer that the first row was exclusive and the second was
        not. A caption and a bounded group say it instead of implying it, which
        also gives a screen reader something to announce the set by.
      */}
      <div className="field">
        <span className="field-label" id={ids.verdict}>
          Record one verdict
        </span>
        <div className="choice" role="group" aria-labelledby={ids.verdict}>
          {/* Presented identically and in a fixed order: NFR-SAFE-2 asks for a
              decision without recommending one. */}
          {(['keep', 'uncertain', 'release', 'invest'] as const).map((intention) => (
            <button
              key={intention}
              type="button"
              className="primary"
              onClick={() => record(intention)}
            >
              {intention === 'uncertain' && uncertainFor === item.itemId
                ? 'Confirm uncertain'
                : intention[0]!.toUpperCase() + intention.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Neither of these decides anything about the item: one defers it and
          the other leaves the session, so they sit apart from the verdicts. */}
      <div className="actions">
        <button type="button" onClick={() => advance(item.itemId, false)}>
          {/* FR-5.9: not now, rather than never again. */}
          Skip
        </button>

        <button type="button" onClick={onLeave}>
          {/* FR-5.10: the session is already saved, so leaving loses nothing --
              and it is still here on the way back. */}
          Pause
        </button>
      </div>
    </div>
  );
}

function ReviewCard({
  replica,
  item,
  remaining,
  decided,
}: {
  replica: Replica;
  item: ReturnType<typeof sessionQueue>[number];
  remaining: number;
  decided: number;
}) {
  const photo = primaryPhoto(item);
  const sources = usePhotoSources(
    replica.db,
    photo === undefined
      ? []
      : [{ photoId: photo.photoId, derivativeKey: photo.metadata?.v.derivativeKey }],
  );
  const source = photo === undefined ? undefined : sources[photo.photoId];
  const descriptors = (['brand', 'color', 'size', 'material'] as const)
    .map((field) => fieldValue(item, field))
    .filter((value): value is string => typeof value === 'string' && value !== '');

  return (
    <>
      <p className="muted" aria-live="polite">
        {decided} decided · {remaining} to go
      </p>

      {/* Photo-forward, as FR-5.6 asks: recognizing the thing is most of
          deciding about it. */}
      {source !== undefined ? (
        <img className="review-photo" src={source} alt={String(fieldValue(item, 'name') ?? '')} />
      ) : (
        <div className="review-photo muted" role="img" aria-label="No photo">
          no photo
        </div>
      )}

      <h2>{String(fieldValue(item, 'name') ?? '(unnamed)')}</h2>
      {descriptors.length > 0 && <p className="muted">{descriptors.join(' · ')}</p>}

      <dl className="sync-state">
        <dt>Now</dt>
        <dd>{item.currentIntention ?? <span className="muted">unreviewed</span>}</dd>
      </dl>
    </>
  );
}

function ScopeForm({
  manifest,
  version,
  scope,
  index,
  onChange,
  onStart,
}: {
  manifest: Manifest;
  version: number;
  scope: ReviewScope;
  index: SearchIndex;
  onChange: (scope: ReviewScope) => void;
  onStart: () => void;
}) {
  const ids = { intention: useId(), since: useId(), brand: useId() };
  const options = facets(manifest);
  const { counts, total } = tallyIntentions(manifest);
  // `version` is in the signature so the parent re-renders this on every change;
  // the count below is read straight from the manifest each time.
  void version;
  const waiting = sessionQueue(manifest, scope, new Set(), index).length;

  return (
    <div className="review">
      <h2>Review</h2>

      {/* FR-5.11: the proportions, shown where someone is about to act on them. */}
      <dl className="sync-state">
        <dt>Unreviewed</dt>
        <dd>
          {counts.unreviewed} of {total}
        </dd>
        <dt>Keep · Uncertain</dt>
        <dd>
          {counts.keep} · {counts.uncertain}
        </dd>
        <dt>Release · Invest</dt>
        <dd>
          {counts.release} · {counts.invest}
        </dd>
      </dl>

      <div className="grid-fields">
        <div className="field">
          <label htmlFor={ids.intention}>Only items that are</label>
          <select
            id={ids.intention}
            value={scope.intention ?? ''}
            onChange={(e) =>
              onChange({
                ...scope,
                intention: e.target.value === '' ? undefined : (e.target.value as Intention),
              })
            }
          >
            {/*
              Only what the queue can contain.
              Keep, Release and Invest were offered here and could never match
              anything: a review is for what has not been decided, so the queue
              holds the unreviewed and the uncertain. A filter whose options
              return nothing is worse than no filter.
            */}
            <option value="">Anything</option>
            <option value="unreviewed">Unreviewed</option>
            <option value="uncertain">Uncertain</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor={ids.since}>Not reviewed for</label>
          <select
            id={ids.since}
            value={scope.notReviewedForDays ?? ''}
            onChange={(e) =>
              onChange({
                ...scope,
                notReviewedForDays: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          >
            <option value="">Any time</option>
            <option value="90">3 months</option>
            <option value="180">6 months</option>
            <option value="365">A year</option>
          </select>
        </div>

        {options.brands.length > 0 && (
          <div className="field">
            <label htmlFor={ids.brand}>Brand</label>
            <select
              id={ids.brand}
              value={scope.brand ?? ''}
              onChange={(e) =>
                onChange({ ...scope, brand: e.target.value === '' ? undefined : e.target.value })
              }
            >
              <option value="">Any</option>
              {options.brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/*
        An empty queue is a finished state, not a broken one.
        It used to read "0 items in this session" above a disabled button, which
        describes the mechanism rather than the situation -- and the queue could
        not empty at all before, because everything already decided was offered
        after the backlog.
      */}
      {waiting === 0 ? (
        <p role="status">
          Nothing to review right now. Items you have not given a verdict yet, and items you marked
          Uncertain, will appear here.
        </p>
      ) : (
        <>
          <p className="muted" aria-live="polite">
            {waiting} item{waiting === 1 ? '' : 's'} in this session.
          </p>

          <div className="actions">
            <button type="button" className="primary" onClick={onStart}>
              Start
            </button>
          </div>
        </>
      )}
    </div>
  );
}
